import type { KeyboardEvent } from "react";
import type {
  HomeLayout,
  HomeWidget,
  HomeWidgetDescriptor,
} from "../../api/hub/home";
import type { LayoutAction } from "./layout";
import { sortedWidgets } from "./layout";
import { widgetTitle } from "./Widget";

const actions: [LayoutAction, string][] = [
  ["left", "左移"],
  ["up", "上移"],
  ["down", "下移"],
  ["right", "右移"],
  ["narrow", "窄一点"],
  ["wide", "宽一点"],
  ["short", "矮一点"],
  ["tall", "高一点"],
];

type Props = {
  layout: HomeLayout;
  descriptors: HomeWidgetDescriptor[];
  selected: HomeWidget | undefined;
  onSelect: (id: string) => void;
  onAdjust: (id: string, action: LayoutAction) => void;
  onConfigure?: (widget: HomeWidget) => void;
  onRemove: (id: string) => void;
  onKeyDown: (event: KeyboardEvent, widget: HomeWidget) => void;
};

export function GridSelectionToolbar({
  layout,
  descriptors,
  selected,
  onSelect,
  onAdjust,
  onConfigure,
  onRemove,
  onKeyDown,
}: Props) {
  const descriptor = descriptors.find((item) => item.type === selected?.type);
  const title = selected ? widgetTitle(selected, descriptor) : "";
  return (
    <section
      className="home-selection-toolbar"
      role="toolbar"
      aria-label="选中部件编辑工具"
      onKeyDown={(event) => selected && onKeyDown(event, selected)}
    >
      <div className="home-selection-heading">
        <label className="home-inline-field">
          当前选中部件
          <select
            value={selected?.id ?? ""}
            disabled={!layout.widgets.length}
            onChange={(event) => onSelect(event.target.value)}
          >
            {!layout.widgets.length && <option value="">尚未添加部件</option>}
            {sortedWidgets(layout).map((widget) => (
              <option key={widget.id} value={widget.id}>
                {widgetTitle(
                  widget,
                  descriptors.find((item) =>
                    item.type === widget.type
                  ),
                )}
              </option>
            ))}
          </select>
        </label>
        {selected && (
          <span className="home-selection-size" aria-live="polite">
            第 {selected.x + 1} 列 / 第 {selected.y + 1} 行 · {selected.w} 列 ×
            {" "}
            {selected.h} 行
          </span>
        )}
      </div>
      {selected
        ? (
          <div className="home-selection-actions">
            {actions.map(([action, label]) => (
              <button
                key={action}
                disabled={!descriptor}
                onClick={() => onAdjust(selected.id, action)}
                aria-label={title + label}
              >
                {label}
              </button>
            ))}
            <button
              disabled={!descriptor}
              onClick={() => onConfigure?.(selected)}
              aria-label={"配置" + title}
            >
              配置
            </button>
            <button
              onClick={() => onRemove(selected.id)}
              aria-label={"移除" + title}
            >
              移除
            </button>
          </div>
        )
        : <p className="home-muted">从部件库添加一个起点。</p>}
    </section>
  );
}
